import { AddToOrderButton } from 'src/shared/components/AddToOrderButton'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Card, Input } from 'src/shared/components/ui'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useOrganizationCurrency } from 'src/shared/hooks/useOrganizationCurrency'

const presetQuantities = [
  { quantity: 10, discount: 0 },
  { quantity: 20, discount: 0 },
  { quantity: 50, discount: 0 },
  { quantity: 100, discount: 0 },
  { quantity: 200, discount: 0 },
  { quantity: 500, discount: 0 },
]

export const QuantitySelector = ({
  quantityInput,
  setQuantityInput,
  price,
  moq,
  product_id,
  chosen_product_variant_color_id,
  product_name,
  addToCurrentQuoteId,
}: {
  quantityInput: number
  setQuantityInput: (quantity: number) => void
  price: number
  moq: number
  product_id: number
  chosen_product_variant_color_id: number
  product_name: string
  addToCurrentQuoteId?: number
}) => {
  const { t } = useLocalization()
  const currency = useOrganizationCurrency()
  const isCustomQuantity = !presetQuantities.some(
    (preset) => preset.quantity === quantityInput
  )

  const customSelected = isCustomQuantity && quantityInput > 0

  return (
    <Card>
      <Column className="gap-2">
        <Column className="gap-2">
          <h4>{t('Choose quantity', 'Välj kvantitet')}</h4>

          <Row className="items-center gap-1">
            <p className="text-xs">
              {t('Minimum order quantity', 'Minimiköp')}:
            </p>
            <p className="text-xs text-foreground/60">{moq}</p>
          </Row>
        </Column>

        <Column className="mt-2 mb-2 gap-2 max-h-[250px] overflow-y-auto p-1">
          {/* Custom quantity input */}
          <div
            className={`w-full text-left rounded-xl border p-3 transition-colors ${
              customSelected
                ? 'border-black ring-1 ring-black bg-black/10'
                : 'border-gray-200 bg-white'
            }`}
          >
            <Row className="items-center justify-between gap-2">
              <Column className="gap-1">
                <Row className="items-center gap-2">
                  <Input
                    type="number"
                    className="h-8 w-[88px] text-sm [&::-webkit-inner-spin-button]:opacity-100 [&::-webkit-outer-spin-button]:opacity-100"
                    value={quantityInput}
                    onChange={(e) => {
                      const value = Number(e.target.value)
                      if (value >= moq || value === 0) {
                        setQuantityInput(value)
                      }
                    }}
                    min={moq || 1}
                  />
                  <span className="text-base font-normal">
                    {t('pieces', 'st')}
                  </span>
                </Row>
              </Column>

              <span className="text-base font-normal">
                {`${Math.round(
                  price * quantityInput
                ).toLocaleString()} ${currency}`}
              </span>
            </Row>
          </div>

          {/* Preset quantity buttons */}
          {presetQuantities.map((preset) => {
            const total = price * preset.quantity
            const selected = quantityInput === preset.quantity

            return (
              <button
                key={preset.quantity}
                type="button"
                onClick={() => setQuantityInput(preset.quantity)}
                className={`w-full text-left rounded-xl border p-3 transition-colors ${
                  selected
                    ? 'border-black ring-1 ring-black bg-gray-100'
                    : 'border-gray-200 hover:border-black bg-white'
                }`}
              >
                <Row className="items-center justify-between gap-2">
                  <Column className="gap-1">
                    <span className="text-base font-normal">
                      {preset.quantity} {t('pieces', 'st')}
                    </span>
                  </Column>

                  <Row className="items-center gap-3">
                    <span className="text-base font-normal">
                      {`${Math.round(total).toLocaleString()} ${currency}`}
                    </span>
                  </Row>
                </Row>
              </button>
            )
          })}
        </Column>

        <AddToOrderButton
          product_id={product_id}
          product_variant_color_id={chosen_product_variant_color_id}
          quantity={quantityInput}
          unit_price={price}
          description={product_name}
          addToCurrentQuoteId={addToCurrentQuoteId}
        />
      </Column>
    </Card>
  )
}
